// Utility functions for PdfTableViewer component

/**
 * Get canvas coordinates from mouse/touch event
 */
export const getCanvasCoordinates = (
  event,
  canvasRef,
  highlightContainerRef
) => {
  if (!canvasRef.current || !highlightContainerRef.current) return null;

  const canvas = canvasRef.current;
  const canvasRect = canvas.getBoundingClientRect();
  const containerRect = highlightContainerRef.current.getBoundingClientRect();

  // Get event coordinates
  const clientX = event.clientX || (event.touches && event.touches[0]?.clientX);
  const clientY = event.clientY || (event.touches && event.touches[0]?.clientY);

  if (clientX === undefined || clientY === undefined) return null;

  // Calculate position relative to canvas
  const canvasOffsetX = canvasRect.left - containerRect.left;
  const canvasOffsetY = canvasRect.top - containerRect.top;

  const x = clientX - containerRect.left - canvasOffsetX;
  const y = clientY - containerRect.top - canvasOffsetY;

  // Check if click is within canvas bounds
  if (x < 0 || y < 0 || x > canvasRect.width || y > canvasRect.height) {
    return null;
  }

  return { x, y };
};

/**
 * Generate unique ID for column or row
 */
export const generateUniqueId = (type, index, preview) => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8);
  const previewSafe =
    preview?.replace(/[^a-zA-Z0-9]/g, "")?.substring(0, 10) || "empty";
  return `${type}_${index}_${previewSafe}_${timestamp}_${random}`;
};

/**
 * Initialize column and row IDs from table data
 */
export const initializeTableIds = (tableData) => {
  if (!tableData?.headers || !tableData?.rows) {
    return { columnIds: [], rowIds: [] };
  }

  const newColumnIds = tableData.headers.map((header, index) => {
    return generateUniqueId("col", index, header);
  });

  const newRowIds = tableData.rows.map((row, index) => {
    const preview = row?.[0] || `row_${index}`;
    return generateUniqueId("row", index, preview);
  });

  return { columnIds: newColumnIds, rowIds: newRowIds };
};

/**
 * Extract original index from ID (for row or column)
 */
export const extractOriginalIndexFromId = (id) => {
  const parts = id?.split("_");
  if (parts?.length >= 2 && (parts[0] === "row" || parts[0] === "col")) {
    const parsedIndex = parseInt(parts[1], 10);
    return isNaN(parsedIndex) ? -1 : parsedIndex;
  }
  return -1;
};

/**
 * Detect if grid lines have structural changes (moved significantly or added/removed)
 */
export const detectGridLineChanges = (originalGridLines, newGridLines) => {
  if (!originalGridLines || !newGridLines) return { hasChanges: false };

  const movedVerticalLines = new Set();
  const movedHorizontalLines = new Set();

  // Check for added/removed vertical lines
  const originalVerticalCount = originalGridLines.vertical?.length || 0;
  const newVerticalCount = newGridLines.vertical?.length || 0;
  const verticalLinesChanged = originalVerticalCount !== newVerticalCount;

  // Check for added/removed horizontal lines
  const originalHorizontalCount = originalGridLines.horizontal?.length || 0;
  const newHorizontalCount = newGridLines.horizontal?.length || 0;
  const horizontalLinesChanged = originalHorizontalCount !== newHorizontalCount;

  // If line count changed, mark all lines as potentially affected
  if (verticalLinesChanged) {
    const maxVerticalLines = Math.max(originalVerticalCount, newVerticalCount);
    for (let i = 0; i < maxVerticalLines; i++) {
      movedVerticalLines.add(i);
    }
  } else {
    // Check for moved vertical lines (>2px threshold)
    const threshold = 2;
    for (let i = 0; i < originalVerticalCount; i++) {
      const original = originalGridLines.vertical[i];
      const current = newGridLines.vertical[i];
      if (original && current) {
        const deltaX = Math.abs(current.x0 - original.x0);
        if (deltaX > threshold) {
          movedVerticalLines.add(i);
        }
      }
    }
  }

  if (horizontalLinesChanged) {
    const maxHorizontalLines = Math.max(
      originalHorizontalCount,
      newHorizontalCount
    );
    for (let i = 0; i < maxHorizontalLines; i++) {
      movedHorizontalLines.add(i);
    }
  } else {
    // Check for moved horizontal lines (>2px threshold)
    const threshold = 2;
    for (let i = 0; i < originalHorizontalCount; i++) {
      const original = originalGridLines.horizontal[i];
      const current = newGridLines.horizontal[i];
      if (original && current) {
        const deltaY = Math.abs(current.y0 - original.y0);
        if (deltaY > threshold) {
          movedHorizontalLines.add(i);
        }
      }
    }
  }

  const hasChanges =
    movedVerticalLines.size > 0 || movedHorizontalLines.size > 0;

  return {
    hasChanges,
    movedVerticalLines,
    movedHorizontalLines,
    verticalLinesChanged,
    horizontalLinesChanged,
  };
};

/**
 * Deep clone grid lines object
 */
export const cloneGridLines = (gridLines) => {
  if (!gridLines) return null;
  return JSON.parse(JSON.stringify(gridLines));
};

/**
 * Calculate insertion position for recovering deleted row/column
 */
export const calculateInsertionPosition = (originalIndex, deletedItems) => {
  let insertPosition = originalIndex;

  // Count how many deleted items come before this one's original position
  Array.from(deletedItems.values()).forEach((itemInfo) => {
    if (itemInfo.originalIndex < originalIndex) {
      insertPosition--;
    }
  });

  return Math.max(0, insertPosition);
};

/**
 * Calculate insertion position for recovering deleted row/column during bulk recovery
 * This version calculates position based on the current visible items, not deleted items
 */
export const calculateBulkRecoveryPosition = (
  originalIndex,
  currentItems,
  allDeletedItems
) => {
  // Count how many items are currently visible that should come before this item
  let visibleItemsBeforeThis = 0;

  // Look at all deleted items to see which ones should come before this item
  // and are not currently deleted (meaning they're already recovered or never deleted)
  for (let i = 0; i < originalIndex; i++) {
    // Check if position i is currently visible (not in deleted items)
    const isCurrentlyDeleted = Array.from(allDeletedItems.values()).some(
      (item) => item.originalIndex === i
    );

    if (!isCurrentlyDeleted) {
      visibleItemsBeforeThis++;
    }
  }

  return Math.max(0, Math.min(visibleItemsBeforeThis, currentItems.length));
};

/**
 * Generate column header name for new columns
 */
export const generateNewColumnHeader = (existingHeaders, baseIndex) => {
  // Try to create a more meaningful name based on the original column
  const originalName = existingHeaders[baseIndex] || `Column${baseIndex + 1}`;

  const baseName = `${originalName}_Split`;
  let counter = 1;
  let newName = baseName;

  // Ensure unique name
  while (existingHeaders.includes(newName)) {
    counter++;
    newName = `${originalName}_Split${counter}`;
  }
  return newName;
};

/**
 * Find the cell that contains the given PDF coordinates by checking word bounding boxes
 * This reverses the highlighting coordinate transformation logic
 */
export const findCellAtPdfCoordinates = (
  pdfX,
  pdfY,
  selectedTable,
  tableData,
  currentPage
) => {
  if (!selectedTable || !tableData || !tableData.headers || !tableData.rows) {
    return null;
  }

  // Check all cells in the table
  const totalRows = 1 + tableData.rows.length; // header row + data rows
  const totalCols = tableData.headers.length;

  for (let rowIndex = 0; rowIndex < totalRows; rowIndex++) {
    for (let colIndex = 0; colIndex < totalCols; colIndex++) {
      const cellKey = `${rowIndex}_${colIndex}`;
      const cellLocationData = selectedTable[cellKey];

      if (
        cellLocationData &&
        Array.isArray(cellLocationData) &&
        cellLocationData.length > 0
      ) {
        const firstCellInfo = cellLocationData[0];

        // Only check cells on the current page
        if (firstCellInfo.page && firstCellInfo.page !== currentPage) {
          continue;
        }

        // Check if any word in this cell contains the click coordinates
        if (firstCellInfo.words && Array.isArray(firstCellInfo.words)) {
          for (const word of firstCellInfo.words) {
            // Get word bounding box coordinates
            let x0, y0, x1, y1;

            if (word.bbox) {
              const bbox = word.bbox;
              if (
                bbox.left !== undefined &&
                bbox.top !== undefined &&
                bbox.width !== undefined &&
                bbox.height !== undefined
              ) {
                // {left, top, width, height} format
                x0 = bbox.left;
                y0 = bbox.top;
                x1 = bbox.left + bbox.width;
                y1 = bbox.top + bbox.height;
              } else {
                // Standard {x0, y0, x1, y1} format
                x0 = bbox.x0 || bbox.x || 0;
                y0 = bbox.y0 || bbox.y || 0;
                x1 = bbox.x1 || bbox.right || 0;
                y1 = bbox.y1 || bbox.bottom || 0;
              }
            } else {
              // Direct coordinate properties
              x0 = word.x0 || word.x || 0;
              y0 = word.y0 || word.y || 0;
              x1 = word.x1 || word.right || 0;
              y1 = word.y1 || word.bottom || 0;
            }

            // Check if click coordinates are within word bounding box
            if (pdfX >= x0 && pdfX <= x1 && pdfY >= y0 && pdfY <= y1) {
              return {
                rowIndex,
                colIndex,
                word: word.text,
                bbox: { x0, y0, x1, y1 },
              };
            }
          }
        }
      }
    }
  }

  return null;
};
